import { useCallback, useEffect, useRef, useState } from 'react';

const empty = () => ({ draft: '', original: '', version: undefined, loaded: false, saving: false, conflict: false, error: null });

function requestKey() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

function parsed(data) {
  if (typeof data?.content !== 'string' || (data.version !== undefined &&
      (!Number.isSafeInteger(data.version) || data.version < 1))) throw new Error('Invalid context response');
  return { draft: data.content, original: data.content, version: data.version, loaded: true,
    saving: false, conflict: false, error: null };
}

// Keep an uncertain save's key for an identical retry. Never apply a response to
// a different agent, nor turn a conflict into a silent last-write-wins overwrite.
export default function useBusinessContext(botId) {
  const [state, setState] = useState(empty);
  const scope = useRef(null);
  if (!scope.current || scope.current.botId !== botId) scope.current = { botId, read: 0, pending: null, saving: false };
  const active = scope.current;
  const url = `${process.env.REACT_APP_API_BASE_URL}business_context/${encodeURIComponent(botId || '')}`;

  const reload = useCallback(async () => {
    if (!botId || active.saving) return false;
    const read = ++active.read;
    setState(s => ({ ...s, loaded: false, error: null }));
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Context unavailable');
      const next = parsed(await response.json());
      if (scope.current !== active || read !== active.read) return false;
      active.pending = null;
      setState(next);
      return true;
    } catch {
      if (scope.current === active && read === active.read) setState(s => ({ ...s, loaded: false, error: 'business_context_load_failed' }));
      return false;
    }
  }, [active, botId, url]);

  useEffect(() => {
    setState(empty());
    reload();
    return () => { active.read += 1; };
  }, [active, reload]);

  const save = async () => {
    if (!botId || !state.loaded || state.conflict || active.saving) return false;
    if (state.draft.length > 1000) {
      setState(s => ({ ...s, error: 'business_context_too_long' }));
      return false;
    }
    const payload = { business_context: state.draft };
    // Older API versions have no version field and retain their legacy contract.
    if (state.version !== undefined) payload.expected_version = state.version;
    const body = JSON.stringify(payload);
    active.saving = true;
    setState(s => ({ ...s, saving: true, error: null }));
    try {
      if (active.pending?.body !== body) active.pending = { body, key: requestKey() };
      const response = await fetch(url, { method: 'PUT', body, headers: {
        'Content-Type': 'application/json', 'Idempotency-Key': active.pending.key,
      } });
      if (!response.ok) {
        const failure = await response.json().catch(() => ({}));
        if (response.status === 409 && failure.detail?.code === 'VERSION_CONFLICT') {
          if (scope.current === active) setState(s => ({ ...s, conflict: true, error: 'business_context_conflict' }));
          return false;
        }
        throw new Error('Context save failed');
      }
      const next = parsed(await response.json());
      if (scope.current !== active) return false;
      active.pending = null;
      setState(next);
      return true;
    } catch {
      if (scope.current === active) setState(s => ({ ...s, error: 'business_context_save_failed' }));
      return false;
    } finally {
      active.saving = false;
      if (scope.current === active) setState(s => ({ ...s, saving: false }));
    }
  };

  return { ...state, save, reload,
    setDraft: draft => { if (!active.saving) setState(s => ({ ...s, draft })); },
    discard: () => { if (!active.saving) setState(s => ({ ...s, draft: s.original })); },
  };
}
